/**
 * Browser-side analysis of a reference video dropped into the Studio.
 *
 * The server sends the whole video to Gemini for the creative read, but a
 * language model is vague about *where* shots start and end, and its idea of
 * "the frame" is a description, not pixels. So before upload we find the exact
 * shot boundaries here and grab one still per shot; those stills are later
 * used as exact framing references for image generation.
 *
 * Method, in three passes over one offscreen <video>:
 *   1. Coarse: seek through the clip at `sampleFps`, reduce every sample to a
 *      tiny signature (colour histogram + blurred luma grid) and score each
 *      neighbouring pair. A cut shows up as an isolated spike; camera or
 *      subject motion shows up as a sustained plateau, which the local-median
 *      baseline absorbs.
 *   2. Refine: for every spike, binary-search between the two samples until
 *      they are one display frame apart. That gives the cut to ~1/60 s and,
 *      as a bonus, a decisive test: across a hard cut nearly all of the coarse
 *      difference lands on a single frame pair, whereas motion spreads it
 *      over every frame in the interval, so the frame-pair score collapses.
 *   3. Grab: seek to 45% into every shot (past any motion blur at the cut, and
 *      before an outgoing move gets going) and encode a full-resolution JPEG.
 *
 * The pure scoring/peak-picking helpers are exported for unit tests.
 */

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ReferenceVideoProbe {
    durationSec: number;
    width: number;
    height: number;
    /** Shot boundaries including both ends: [0, c1, c2, ..., duration]. */
    cuts: number[];
    /** One JPEG per shot, taken ~45% into the shot, long side capped at 1600 px. */
    frames: Array<{ t: number; blob: Blob }>;
    /** Small JPEG of the first shot, for cards and lists. */
    poster: Blob;
}

export interface ProbeOptions {
    onProgress?: (p: number) => void;
    /** Coarse sampling rate. 10 fps keeps every shot of >= 0.2 s visible at least twice. */
    sampleFps?: number;
    /** Absolute floor for a coarse score to count as a cut candidate (0..1). */
    threshold?: number;
}

/** Long side of the per-shot stills. */
const FRAME_MAX_SIDE = 1600;
/** Long side of the poster. */
const POSTER_MAX_SIDE = 640;
/** Where inside a shot the still is taken (fraction of its length). */
const SHOT_GRAB_POINT = 0.45;

export async function probeReferenceVideo(file: File | string, opts: ProbeOptions = {}): Promise<ReferenceVideoProbe> {
    const onProgress = opts.onProgress || (() => undefined);
    const ownedUrl = typeof file === 'string' ? null : URL.createObjectURL(file);
    const src = ownedUrl || (file as string);
    const player = await VideoSeeker.open(src);
    try {
        const { width, height, duration } = player;
        const detection = await detectCuts(player, {
            sampleFps: opts.sampleFps,
            threshold: opts.threshold,
            onProgress: p => onProgress(p * 0.85),
        });
        const cuts = [0, ...detection.cuts, duration];

        const grabTimes = shotGrabTimes(cuts, SHOT_GRAB_POINT);
        const full = fitWithin(width, height, FRAME_MAX_SIDE);
        const canvas = document.createElement('canvas');
        canvas.width = full.width;
        canvas.height = full.height;
        const ctx = canvas.getContext('2d');
        const frames: Array<{ t: number; blob: Blob }> = [];
        let poster: Blob | null = null;
        for (let i = 0; i < grabTimes.length; i++) {
            const t = grabTimes[i];
            await player.seek(t);
            ctx.drawImage(player.video, 0, 0, full.width, full.height);
            frames.push({ t: round3(t), blob: await canvasToJpeg(canvas, 0.9) });
            if (i === 0) poster = await downscaleToJpeg(canvas, POSTER_MAX_SIDE, 0.85);
            onProgress(0.85 + (0.15 * (i + 1)) / grabTimes.length);
        }

        return { durationSec: duration, width, height, cuts: cuts.map(round3), frames, poster };
    } finally {
        player.dispose();
        if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    }
}

export async function probeReferenceImage(file: File): Promise<{ width: number; height: number }> {
    // createImageBitmap honours EXIF orientation, so a portrait phone photo
    // reports portrait dimensions, matching how it will be displayed.
    if (typeof createImageBitmap === 'function') {
        try {
            const bmp = await createImageBitmap(file);
            const size = { width: bmp.width, height: bmp.height };
            bmp.close();
            return size;
        } catch {
            // Some formats (SVG, older Safari HEIC) need the <img> path below.
        }
    }
    const url = URL.createObjectURL(file);
    try {
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Could not read the image'));
            img.src = url;
        });
        return { width: img.naturalWidth, height: img.naturalHeight };
    } finally {
        URL.revokeObjectURL(url);
    }
}

// ---------------------------------------------------------------------------
// Cut detection (needs a video; exported for the browser test harness)
// ---------------------------------------------------------------------------

export interface CutDiagnostics {
    /** Coarse sample times and the score between sample i-1 and i (score[0] = 0). */
    times: number[];
    scores: number[];
    /** Every spike that was refined, with its frame-pair score and the verdict. */
    candidates: Array<{ coarseT: number; coarse: number; cut: number; fine: number; split: boolean; accepted: boolean }>;
}

export async function detectCuts(
    player: VideoSeeker,
    opts: { sampleFps?: number; threshold?: number; onProgress?: (p: number) => void } = {},
): Promise<{ cuts: number[] } & CutDiagnostics> {
    const sampleFps = opts.sampleFps || 10;
    const onProgress = opts.onProgress || (() => undefined);
    const { duration } = player;
    const sig = new SignatureSampler(player);

    // Pass 1: coarse samples. Offset by half a step so no sample sits exactly
    // on t=0 (some encoders put a black/priming frame there).
    const times = coarseSampleTimes(duration, sampleFps);
    const sigs: FrameSignature[] = [];
    const frameTimes: Array<number | null> = [];
    const scores: number[] = [];
    for (let i = 0; i < times.length; i++) {
        sigs.push(await sig.at(times[i]));
        frameTimes.push(sig.lastMediaTime);
        scores.push(i === 0 ? 0 : frameDistance(sigs[i - 1], sigs[i]));
        onProgress((0.8 * (i + 1)) / times.length);
    }

    const picked = pickCutCandidates(scores, times, {
        threshold: opts.threshold,
        minShotSec: MIN_SHOT_SEC,
        duration,
    });

    // Pass 2: refine each spike to a frame pair and keep only hard cuts.
    const candidates: CutDiagnostics['candidates'] = [];
    const cuts: number[] = [];
    const refineStep = 1 / 60;
    for (let n = 0; n < picked.length; n++) {
        const i = picked[n];
        let ta = times[i - 1];
        let tb = times[i];
        let a = sigs[i - 1];
        let b = sigs[i];
        let bTime = frameTimes[i];
        let split = true;
        while (tb - ta > refineStep) {
            const m = (ta + tb) / 2;
            const s = await sig.at(m);
            const dA = frameDistance(s, a);
            const dB = frameDistance(s, b);
            // Across a hard cut the middle frame is plainly one shot or the
            // other. If it is about as far from both ends, the change is spread
            // over time (motion, a dissolve): stop seeking, it is not a cut.
            if (!belongsToOneSide(dA, dB)) {
                split = false;
                break;
            }
            // Whichever side the middle frame resembles, the cut is on the
            // other side of it.
            if (dA <= dB) {
                ta = m;
                a = s;
            } else {
                tb = m;
                b = s;
                bTime = sig.lastMediaTime;
            }
        }
        const fine = frameDistance(a, b);
        const accepted = split && isHardCut(fine, scores[i], opts.threshold);
        // Prefer the presented frame's own timestamp (rVFC) — it is the first
        // frame of the new shot, which is what ffmpeg & editors call the cut.
        const cut = bTime != null && bTime > ta && bTime <= tb + 1e-3 ? bTime : tb;
        candidates.push({ coarseT: times[i], coarse: round3(scores[i]), cut: round3(cut), fine: round3(fine), split, accepted });
        if (accepted) cuts.push(cut);
        onProgress(0.8 + (0.2 * (n + 1)) / picked.length);
    }

    return { cuts: enforceMinShot(cuts, duration, MIN_SHOT_SEC), times, scores, candidates };
}

// ---------------------------------------------------------------------------
// Pure helpers (unit tested)
// ---------------------------------------------------------------------------

/** Shots shorter than this are flashes, glitches or a priming frame, not shots. */
export const MIN_SHOT_SEC = 0.4;
/** Default absolute floor for a coarse score to become a candidate. */
export const DEFAULT_THRESHOLD = 0.12;

/** Width of the signature canvas; height follows the aspect ratio. */
export const SIGNATURE_WIDTH = 32;
/** Luma block grid width (blocks are square, so height follows aspect). */
const BLOCK_COLS = 8;

const HUE_BINS = 12;
const SAT_BINS = 3;
const VAL_BINS = 3;
const GREY_BINS = 5;
export const HIST_BINS = HUE_BINS * SAT_BINS * VAL_BINS + GREY_BINS;

export interface FrameSignature {
    /** Normalised HSV histogram (sums to 1). Blind to where things are, hence motion-proof. */
    hist: Float32Array;
    /** Mean luma per block of a coarse grid. Catches cuts between same-palette shots. */
    blocks: Float32Array;
    blockCols: number;
    blockRows: number;
}

export function computeSignature(rgba: ArrayLike<number>, width: number, height: number): FrameSignature {
    const hist = new Float32Array(HIST_BINS);
    const blockCols = Math.min(BLOCK_COLS, width);
    const blockRows = Math.max(1, Math.round((blockCols * height) / width));
    const blocks = new Float32Array(blockCols * blockRows);
    const counts = new Float32Array(blockCols * blockRows);
    const n = width * height;
    for (let y = 0; y < height; y++) {
        const by = Math.min(blockRows - 1, Math.floor((y * blockRows) / height));
        for (let x = 0; x < width; x++) {
            const p = (y * width + x) * 4;
            const r = rgba[p] / 255;
            const g = rgba[p + 1] / 255;
            const b = rgba[p + 2] / 255;
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const v = max;
            const s = max === 0 ? 0 : (max - min) / max;
            // Near-grey or near-black pixels have meaningless hue: bin them by
            // brightness alone so sensor noise cannot flip them between hues.
            if (s < 0.15 || v < 0.12) {
                hist[HUE_BINS * SAT_BINS * VAL_BINS + Math.min(GREY_BINS - 1, Math.floor(v * GREY_BINS))] += 1;
            } else {
                let h: number;
                const d = max - min;
                if (max === r) h = ((g - b) / d + 6) % 6;
                else if (max === g) h = (b - r) / d + 2;
                else h = (r - g) / d + 4;
                const hb = Math.min(HUE_BINS - 1, Math.floor((h / 6) * HUE_BINS));
                const sb = Math.min(SAT_BINS - 1, Math.floor(((s - 0.15) / 0.85) * SAT_BINS));
                const vb = Math.min(VAL_BINS - 1, Math.floor(((v - 0.12) / 0.88) * VAL_BINS));
                hist[(hb * SAT_BINS + sb) * VAL_BINS + vb] += 1;
            }
            const bx = Math.min(blockCols - 1, Math.floor((x * blockCols) / width));
            const bi = by * blockCols + bx;
            blocks[bi] += 0.299 * r + 0.587 * g + 0.114 * b;
            counts[bi] += 1;
        }
    }
    for (let i = 0; i < HIST_BINS; i++) hist[i] /= n || 1;
    for (let i = 0; i < blocks.length; i++) blocks[i] /= counts[i] || 1;
    return { hist, blocks, blockCols, blockRows };
}

/** Symmetric chi-square distance between two normalised histograms, in [0, 1]. */
export function histogramDistance(a: ArrayLike<number>, b: ArrayLike<number>): number {
    let d = 0;
    for (let i = 0; i < a.length; i++) {
        const s = a[i] + b[i];
        if (s > 0) d += ((a[i] - b[i]) * (a[i] - b[i])) / s;
    }
    return d / 2;
}

/** Mean absolute difference of the block luma grids, in [0, 1]. */
export function blockDistance(a: ArrayLike<number>, b: ArrayLike<number>): number {
    let d = 0;
    for (let i = 0; i < a.length; i++) d += Math.abs(a[i] - b[i]);
    return a.length ? d / a.length : 0;
}

/**
 * One score in [0, 1] for how different two frames are. The histogram term is
 * the motion-robust backbone; the block term (scaled up, since a 0.25 mean
 * luma swing is already a different picture) rescues cuts between two shots
 * that share a palette, e.g. the same room from another angle.
 */
export function frameDistance(a: FrameSignature, b: FrameSignature): number {
    const h = histogramDistance(a.hist, b.hist);
    const k = Math.min(1, blockDistance(a.blocks, b.blocks) * 4);
    return Math.min(1, 0.6 * h + 0.4 * k);
}

export function median(xs: ArrayLike<number>): number {
    if (!xs.length) return 0;
    const s = Array.prototype.slice.call(xs).sort((p: number, q: number) => p - q);
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export interface PeakOptions {
    /** Absolute floor; defaults to DEFAULT_THRESHOLD. */
    threshold?: number;
    /** A spike must beat the local median by this factor. */
    k?: number;
    /** Half-width of the local-median window, in samples. */
    window?: number;
    minShotSec?: number;
    /** When given, spikes closer than minShotSec to either end are dropped. */
    duration?: number;
}

/**
 * Indices i where score[i] (the change between sample i-1 and i) is a cut
 * candidate: above the absolute floor and clearly above the local median, so
 * a stretch of steady motion raises its own bar. Deliberately permissive:
 * under fast handheld motion a real cut can score no higher than the motion
 * around it, and only the frame-level refinement can tell them apart — so we
 * refine every plausible spike and let refinement reject (cheaply, after one
 * seek) the ones that are motion.
 */
export function pickCutCandidates(scores: ArrayLike<number>, times: ArrayLike<number>, opts: PeakOptions = {}): number[] {
    const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
    const k = opts.k ?? 1.5;
    const win = opts.window ?? 8;
    const minShot = opts.minShotSec ?? MIN_SHOT_SEC;
    const kept: number[] = [];
    for (let i = 1; i < scores.length; i++) {
        const s = scores[i];
        if (s < threshold) continue;
        const neighbours: number[] = [];
        for (let j = Math.max(1, i - win); j <= Math.min(scores.length - 1, i + win); j++) if (j !== i) neighbours.push(scores[j]);
        if (s < k * median(neighbours)) continue;
        kept.push(i);
    }
    if (opts.duration == null) return kept;
    // The cut lies in (times[i-1], times[i]]; drop it if the shot before or
    // after it would be shorter than minShot whatever the exact position.
    return kept.filter(i => times[i] >= minShot && times[i - 1] <= opts.duration - minShot);
}

/**
 * Refinement step test: does the middle frame clearly resemble one end of the
 * interval? `dA`/`dB` are its distances to the frames at either end.
 */
export function belongsToOneSide(dA: number, dB: number): boolean {
    return Math.min(dA, dB) <= 0.5 * Math.max(dA, dB);
}

/**
 * Hard-cut test on the refined frame pair. Across a real cut the whole coarse
 * difference happens between two adjacent frames; with motion, two adjacent
 * frames are nearly identical however large the coarse jump was.
 */
export function isHardCut(fine: number, coarse: number, threshold: number = DEFAULT_THRESHOLD): boolean {
    return fine >= threshold && fine >= 0.6 * coarse;
}

/** Drop cuts that would leave a shot shorter than minShot (keeps the earlier one). */
export function enforceMinShot(cuts: number[], duration: number, minShot: number = MIN_SHOT_SEC): number[] {
    const out: number[] = [];
    let prev = 0;
    for (const c of [...cuts].sort((p, q) => p - q)) {
        if (c - prev < minShot || duration - c < minShot) continue;
        out.push(c);
        prev = c;
    }
    return out;
}

export function coarseSampleTimes(duration: number, fps: number): number[] {
    const step = 1 / fps;
    const out: number[] = [];
    for (let t = step / 2; t < duration - 1e-3; t += step) out.push(round3(t));
    return out;
}

/** The moment to grab in every shot, given boundaries [0, c1, ..., duration]. */
export function shotGrabTimes(bounds: number[], at: number = SHOT_GRAB_POINT): number[] {
    const out: number[] = [];
    for (let i = 0; i + 1 < bounds.length; i++) out.push(bounds[i] + (bounds[i + 1] - bounds[i]) * at);
    return out;
}

export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
    const scale = Math.min(1, maxSide / Math.max(width, height));
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

function round3(x: number): number {
    return Math.round(x * 1000) / 1000;
}

// ---------------------------------------------------------------------------
// Browser plumbing
// ---------------------------------------------------------------------------

type FrameCallbackVideo = HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
    cancelVideoFrameCallback?: (handle: number) => void;
};

/**
 * An offscreen <video> that can be seeked frame-accurately and awaited.
 *
 * All times it takes and reports are measured from the FIRST FRAME, not from
 * the container's zero. Some MP4s (e.g. Pinterest exports) start their
 * streams at 0.1 s; Chrome then shows the first frame at currentTime 0.1,
 * while ffmpeg, editors and the server-side model all count from the first
 * frame. Normalising here keeps our cuts on the same clock as theirs.
 */
export class VideoSeeker {
    readonly video: FrameCallbackVideo;
    width = 0;
    height = 0;
    /** Length from the first frame to the end. */
    duration = 0;
    /** Media time of the first frame (0 for most files). */
    start = 0;
    /** Timestamp (first-frame clock) of the frame the last seek presented, when rVFC told us. */
    lastMediaTime: number | null = null;

    private constructor(video: FrameCallbackVideo) {
        this.video = video;
    }

    static async open(src: string): Promise<VideoSeeker> {
        const video = document.createElement('video') as FrameCallbackVideo;
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.crossOrigin = 'anonymous'; // keeps the canvas untainted for remote URLs
        video.setAttribute('aria-hidden', 'true');
        // In the document but invisible: detached or display:none videos never
        // present frames, so requestVideoFrameCallback would never fire.
        video.style.cssText =
            'position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none;z-index:-1';
        document.body.appendChild(video);
        const seeker = new VideoSeeker(video);
        // The first presented frame reports its own timestamp: that is the
        // offset between the container clock and the first-frame clock.
        let firstFrameTime: number | null = null;
        if (video.requestVideoFrameCallback) {
            video.requestVideoFrameCallback((_now, meta) => { firstFrameTime = meta.mediaTime; });
        }
        try {
            await new Promise<void>((resolve, reject) => {
                const timer = setTimeout(() => reject(new Error('Timed out loading the video')), 30000);
                video.addEventListener('loadeddata', () => { clearTimeout(timer); resolve(); }, { once: true });
                video.addEventListener('error', () => {
                    clearTimeout(timer);
                    reject(new Error('This video format cannot be read in the browser'));
                }, { once: true });
                video.src = src;
            });
            // MediaRecorder WebMs report Infinity until the browser has seen the
            // end; seeking far past it makes the browser work the length out.
            if (!Number.isFinite(video.duration)) {
                await new Promise<void>(resolve => {
                    video.addEventListener('durationchange', () => resolve(), { once: true });
                    video.currentTime = 1e9;
                });
            }
            if (video.requestVideoFrameCallback && firstFrameTime == null) {
                // Usually presented within a frame of loadeddata; never block on it.
                for (let i = 0; i < 10 && firstFrameTime == null; i++) await new Promise(r => setTimeout(r, 25));
            }
            // Only trust small offsets: a large one means the callback caught a
            // later frame (e.g. after the Infinity-duration probe above).
            seeker.start = firstFrameTime != null && firstFrameTime > 0 && firstFrameTime < 1 ? firstFrameTime : 0;
            seeker.width = video.videoWidth;
            seeker.height = video.videoHeight;
            seeker.duration = video.duration - seeker.start;
            if (!seeker.width || !seeker.height || !(seeker.duration > 0)) throw new Error('The video has no picture');
            return seeker;
        } catch (e) {
            seeker.dispose();
            throw e;
        }
    }

    /**
     * Seek and resolve once the target frame can be drawn. `seeked` alone is
     * enough in Chrome and Firefox, but Safari may still paint the previous
     * frame; requestVideoFrameCallback fires when the new one is actually
     * presented, and also tells us its exact timestamp. It does not fire when
     * the tab is hidden or the seek lands on the frame already shown, so
     * `seeked` plus a short grace period is the fallback.
     */
    seek(t: number): Promise<void> {
        const v = this.video;
        const target = this.start + Math.max(0, Math.min(t, this.duration - 0.001));
        this.lastMediaTime = null;
        return new Promise<void>((resolve, reject) => {
            let seeked = false;
            let framed = !v.requestVideoFrameCallback || (typeof document !== 'undefined' && document.hidden);
            let grace: ReturnType<typeof setTimeout> | null = null;
            let handle: number | null = null;
            // A decoder that never answers must fail the probe, not hang it.
            const stall = setTimeout(() => {
                cleanup();
                reject(new Error('The video stopped responding while seeking'));
            }, 10000);
            const cleanup = () => {
                clearTimeout(stall);
                if (grace) clearTimeout(grace);
                if (handle != null && v.cancelVideoFrameCallback) v.cancelVideoFrameCallback(handle);
                v.removeEventListener('seeked', onSeeked);
                v.removeEventListener('error', onError);
            };
            const finish = () => {
                cleanup();
                resolve();
            };
            const onSeeked = () => {
                seeked = true;
                // The tab may have been hidden mid-probe: no frames get presented then.
                if (framed || document.hidden) finish();
                else grace = setTimeout(finish, 50);
            };
            const onError = () => {
                cleanup();
                reject(new Error('The video stopped decoding'));
            };
            if (!framed) {
                handle = v.requestVideoFrameCallback((_now, meta) => {
                    handle = null;
                    framed = true;
                    this.lastMediaTime = meta.mediaTime - this.start;
                    if (seeked) finish();
                });
            }
            v.addEventListener('seeked', onSeeked);
            v.addEventListener('error', onError);
            v.currentTime = target;
        });
    }

    dispose(): void {
        const v = this.video;
        v.pause();
        v.removeAttribute('src');
        v.load(); // releases the decoder and the network connection
        v.remove();
    }
}

/** Draws the current video frame into a tiny canvas and fingerprints it. */
class SignatureSampler {
    private readonly ctx: CanvasRenderingContext2D;
    private readonly w: number;
    private readonly h: number;

    constructor(private readonly player: VideoSeeker) {
        this.w = SIGNATURE_WIDTH;
        this.h = Math.max(1, Math.round((SIGNATURE_WIDTH * player.height) / player.width));
        const canvas = document.createElement('canvas');
        canvas.width = this.w;
        canvas.height = this.h;
        // willReadFrequently keeps the canvas on the CPU, which makes the
        // hundreds of getImageData calls cheap.
        this.ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
        this.ctx.imageSmoothingQuality = 'low';
    }

    get lastMediaTime(): number | null {
        return this.player.lastMediaTime;
    }

    async at(t: number): Promise<FrameSignature> {
        await this.player.seek(t);
        this.ctx.drawImage(this.player.video, 0, 0, this.w, this.h);
        return computeSignature(this.ctx.getImageData(0, 0, this.w, this.h).data, this.w, this.h);
    }
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) =>
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not encode the frame'))), 'image/jpeg', quality),
    );
}

function downscaleToJpeg(source: HTMLCanvasElement, maxSide: number, quality: number): Promise<Blob> {
    const size = fitWithin(source.width, source.height, maxSide);
    const c = document.createElement('canvas');
    c.width = size.width;
    c.height = size.height;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, size.width, size.height);
    return canvasToJpeg(c, quality);
}
